import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CareerTimeline = () => {
  const [expandedRole, setExpandedRole] = useState(null);

  const timelineData = [
    {
      id: 1,
      title: 'Senior MERN Stack Developer | Technical Consultant',
      company: 'Cosmic Info Solution',
      location: 'Surat, IN',
      period: '2023 - Present',
      type: 'Full-time',
      logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop',
      description: `Leading technical discovery, architecture design, and project lifecycle for MERN stack solutions.`,
      achievements: [
        'Advised clients on digital transformation and streamlined workflows',
        'Designed scalable frontend and backend MERN architectures',
        'Managed project roadmaps and deployment optimization',
        'Provided continuous post-launch support and consulting'
      ],
      technologies: ['React', 'Node.js', 'Express.js', 'MongoDB', 'AWS'],
      current: true
    },
    {
      id: 2,
      title: 'Lead MERN Stack Developer',
      company: 'Iqonic Design',
      location: 'Surat, IN',
      period: '2021 - 2023',
      type: 'Full-time',
      logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop',
      description: `Led the ground-up MERN Stack implementation of KiviCare, transforming it from a WordPress product.`,
      achievements: [
        'Architected overall solution including MongoDB schema and React components',
        'Directed development of core healthcare management modules',
        'Successfully delivered the complete MERN implementation within 1 month',
        'Optimized performance and planned production deployments'
      ],
      technologies: ['React', 'Node.js', 'MongoDB', 'REST APIs', 'WordPress'],
      current: false
    }
  ];

  const toggleExpanded = (roleId) => {
    setExpandedRole(expandedRole === roleId ? null : roleId);
  };

  return (
    <div className="bg-surface rounded-lg p-4 sm:p-6 shadow-md border border-border overflow-hidden">
      <div className="flex items-center mb-6">
        <Icon name="Clock" size={24} className="text-primary mr-3" />
        <h2 className="text-2xl font-bold text-text-primary">Career Timeline</h2>
      </div>

      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden md:block"></div>

        <div className="space-y-6">
          {timelineData.map((role, index) => (
            <div key={role.id} className="relative">
              {/* Timeline Dot */}
              <div className="absolute left-6 w-4 h-4 bg-primary rounded-full border-4 border-background hidden md:block"></div>
              
              <div className="md:ml-16">
                <div className="bg-muted rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow duration-200 overflow-hidden">
                  {/* Role Header */}
                  <div className="flex flex-col sm:flex-row items-start justify-between mb-4 gap-3">
                    <div className="flex items-start space-x-4 min-w-0 flex-1">
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <Image
                          src={role.logo}
                          alt={`${role.company} logo`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-text-primary break-words">{role.title}</h3>
                        <p className="text-primary font-medium">{role.company}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-text-secondary mt-1">
                          <div className="flex items-center">
                            <Icon name="MapPin" size={14} className="mr-1 flex-shrink-0" />
                            {role.location}
                          </div>
                          <div className="flex items-center">
                            <Icon name="Calendar" size={14} className="mr-1 flex-shrink-0" />
                            {role.period}
                          </div>
                          <div className="flex items-center">
                            <Icon name="Briefcase" size={14} className="mr-1 flex-shrink-0" />
                            {role.type}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {role.current && (
                      <div className="bg-success text-white px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0">
                        Current
                      </div>
                    )}
                  </div>

                  {/* Role Description */}
                  <p className="text-text-secondary mb-4 leading-relaxed">{role.description}</p>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {role.technologies.map((tech) => (
                      <span key={tech} className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Expand/Collapse Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleExpanded(role.id)}
                    className="mb-4"
                  >
                    <Icon 
                      name={expandedRole === role.id ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                      className="mr-2" 
                    />
                    {expandedRole === role.id ? 'Show Less' : 'Show Achievements'}
                  </Button>

                  {/* Expanded Content */}
                  {expandedRole === role.id && (
                    <div className="border-t border-border pt-4">
                      <h4 className="font-semibold text-text-primary mb-3">Key Achievements</h4>
                      <ul className="space-y-2">
                        {role.achievements.map((achievement, achievementIndex) => (
                          <li key={achievementIndex} className="flex items-start">
                            <Icon name="CheckCircle" size={16} className="text-success mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-text-secondary text-sm">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline Summary */}
      <div className="mt-8 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 text-center">
          <div className="p-2">
            <div className="text-xl sm:text-2xl font-bold text-primary">4.5+</div>
            <div className="text-xs sm:text-sm text-text-secondary">Years Experience</div>
          </div>
          <div className="p-2">
            <div className="text-xl sm:text-2xl font-bold text-success">1</div>
            <div className="text-xs sm:text-sm text-text-secondary">Companies</div>
          </div>
          <div className="p-2">
            <div className="text-xl sm:text-2xl font-bold text-accent">40+</div>
            <div className="text-xs sm:text-sm text-text-secondary">Projects</div>
          </div>
          <div className="p-2">
            <div className="text-xl sm:text-2xl font-bold text-warning">10+</div>
            <div className="text-xs sm:text-sm text-text-secondary">Technologies</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerTimeline;
